import { useState } from 'react'
import { MessageSquare, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { OrderDetails, Service, SurveyResponse, TemplateQuestion } from '@/api/orderDetails'
import { useLocalization } from '@/contexts/LocalizationContext'
import { formatOrderDateTime } from '@/hooks/useOrders'
import {
  CLIENT_ANSWERS_COLLAPSE_THRESHOLD,
  groupAnswersByUuid,
} from '@/utils/orderSurveyGrouping'
import { getPickupDropoffGoogleMapsUrl, serviceHasSurveyContent } from '@/utils/orderSurveyDisplay'
import { getDisplayNameForGroup } from '@/utils/surveyQuestionDisplayNames'
import { bucketSurveyResponses } from '@/utils/orderSurveyResponses'
import { SurveySection } from './SurveySection'
import { SurveyQuestionDisplay } from './SurveyQuestionDisplay'
import { PendingSurveyCard } from './PendingSurveyCard'
import { SurveyResponseCard } from './SurveyResponseCard'

function getTemplateQuestionsForLookup(service: Service): TemplateQuestion[] {
  return [
    ...(service.pendingSurveys?.flatMap((s) => s.survey?.templateQuestions ?? []) ?? []),
    ...(service.serviceTemplate?.surveyAssignments?.flatMap((a) => a.survey?.templateQuestions ?? []) ?? []),
  ]
}

function SubmittedResponsesSection({
  title,
  responses,
  locale,
  templateQuestionsForLookup,
  accent,
  defaultOpen = true,
}: {
  title: string
  responses: SurveyResponse[]
  locale: 'en' | 'ar'
  templateQuestionsForLookup: TemplateQuestion[]
  accent: 'green' | 'amber' | 'blue' | 'purple'
  defaultOpen?: boolean
}) {
  if (responses.length === 0) return null
  return (
    <SurveySection title={title} count={responses.length} dotColor="bg-green-500" defaultOpen={defaultOpen}>
      <div className="space-y-3">
        {responses.map((response) => (
          <SurveyResponseCard
            key={response.id}
            response={response}
            locale={locale}
            templateQuestionsForLookup={templateQuestionsForLookup}
            accent={accent}
          />
        ))}
      </div>
    </SurveySection>
  )
}

function ServiceSurveyCard({
  service,
  index,
  locale,
  expandedClientAnswers,
  onToggleClientAnswers,
}: {
  service: Service
  index: number
  locale: 'en' | 'ar'
  expandedClientAnswers: boolean
  onToggleClientAnswers: () => void
}) {
  const { t } = useLocalization()
  const templateQuestionsForLookup = getTemplateQuestionsForLookup(service)

  const answers = service.clientAnswersWithQuestions ?? []
  const { groups, ungrouped } = groupAnswersByUuid(answers)
  const orderedAnswerList = ungrouped.concat(groups.flatMap((g) => g.answers))
  const showCollapse = answers.length > CLIENT_ANSWERS_COLLAPSE_THRESHOLD
  const visibleAnswers =
    showCollapse && !expandedClientAnswers
      ? orderedAnswerList.slice(0, CLIENT_ANSWERS_COLLAPSE_THRESHOLD)
      : orderedAnswerList
  const visibleSet = new Set(visibleAnswers)
  const hiddenCount = answers.length - visibleAnswers.length
  const routeUrl = getPickupDropoffGoogleMapsUrl(answers)

  const responseBuckets = bucketSurveyResponses(service.clientSurveyResponses ?? [], locale)
  const hasProtocol = responseBuckets.protocol.length > 0
  const hasProvider = responseBuckets.provider.length > 0
  const hasClientResponses = responseBuckets.client.length > 0
  const useBuckets = hasProtocol || hasProvider || hasClientResponses

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/50 px-5 py-3 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold text-white bg-brand-primary shrink-0">
            {index + 1}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate">{service.serviceName}</p>
            <p className="text-xs text-gray-500">{service.serviceCategory}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {service.hasPendingSurvey && service.pendingSurveyCount > 0 && (
            <Badge className="bg-yellow-100 text-yellow-800 border-0">
              {service.pendingSurveyCount} {t('orderDetails.pendingSurveys')}
            </Badge>
          )}
          {service.appointmentScheduledStart && (
            <span className="text-xs text-gray-500 hidden sm:inline">
              {formatOrderDateTime(service.appointmentScheduledStart, locale)}
            </span>
          )}
        </div>
      </div>

      <div className="p-5 space-y-1">
        {answers.length > 0 && (
          <SurveySection
            title={t('orderDetails.clientAnswers')}
            count={answers.length}
            dotColor="bg-blue-500"
          >
            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-3 space-y-3">
              {routeUrl && (
                <a
                  href={routeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-brand-primary hover:underline block"
                >
                  {t('orderDetails.viewPickupDropoffRoute')}
                </a>
              )}

              {ungrouped.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                  <div className="border-b border-gray-100 bg-gray-50 px-3 py-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t('orderDetails.answerGroup')} 1 — {t('orderDetails.questionsWithoutIds')}
                    </p>
                  </div>
                  <div className="p-3 space-y-3">
                    {ungrouped.filter((a) => visibleSet.has(a)).map((answer, idx) => (
                      <SurveyQuestionDisplay
                        key={`${answer.templateQuestionId}-ungrouped-${idx}`}
                        answer={answer}
                        locale={locale}
                        templateQuestionsForLookup={templateQuestionsForLookup}
                      />
                    ))}
                  </div>
                </div>
              )}

              {groups.map(({ uuid, answers: groupAnswers }, groupIndex) => {
                const visibleInGroup = groupAnswers.filter((a) => visibleSet.has(a))
                if (visibleInGroup.length === 0) return null
                const groupNumber = groupIndex + 2
                const groupDisplayName = getDisplayNameForGroup(uuid, groupAnswers, locale)
                return (
                  <div key={uuid} className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                    <div className="border-b border-gray-100 bg-gray-50 px-3 py-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {t('orderDetails.answerGroup')} {groupNumber} — {groupDisplayName ?? uuid}
                      </p>
                    </div>
                    <div className="p-3 space-y-3">
                      {visibleInGroup.map((answer, idx) => (
                        <SurveyQuestionDisplay
                          key={`${answer.templateQuestionId}-${idx}`}
                          answer={answer}
                          locale={locale}
                          templateQuestionsForLookup={templateQuestionsForLookup}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}

              {showCollapse && hiddenCount > 0 && (
                <div className="text-center">
                  <Button variant="ghost" size="sm" className="text-gray-500" onClick={onToggleClientAnswers}>
                    <ChevronDown
                      className={`h-4 w-4 me-1 transition-transform ${expandedClientAnswers ? 'rotate-180' : ''}`}
                    />
                    {expandedClientAnswers
                      ? t('orderDetails.hideAnswers')
                      : t('orderDetails.showMoreAnswers', { count: hiddenCount })}
                  </Button>
                </div>
              )}
            </div>
          </SurveySection>
        )}

        {service.pendingSurveys && service.pendingSurveys.length > 0 && (
          <SurveySection
            title={t('orderDetails.pendingSurveys')}
            count={service.pendingSurveys.length}
            dotColor="bg-yellow-500"
          >
            <div className="space-y-3">
              {service.pendingSurveys.map((survey) => (
                <PendingSurveyCard key={survey.id} survey={survey} />
              ))}
            </div>
          </SurveySection>
        )}

        {useBuckets ? (
          <>
            <SubmittedResponsesSection
              title={t('orderDetails.providerFeedback')}
              responses={responseBuckets.provider}
              locale={locale}
              templateQuestionsForLookup={templateQuestionsForLookup}
              accent="amber"
              defaultOpen
            />
            <SubmittedResponsesSection
              title={t('orderDetails.protocolSurveys')}
              responses={responseBuckets.protocol}
              locale={locale}
              templateQuestionsForLookup={templateQuestionsForLookup}
              accent="purple"
              defaultOpen
            />
            <SubmittedResponsesSection
              title={t('orderDetails.clientFeedback')}
              responses={responseBuckets.client}
              locale={locale}
              templateQuestionsForLookup={templateQuestionsForLookup}
              accent="green"
              defaultOpen
            />
            <SubmittedResponsesSection
              title={t('orderDetails.submittedAnswers')}
              responses={responseBuckets.other}
              locale={locale}
              templateQuestionsForLookup={templateQuestionsForLookup}
              accent="blue"
              defaultOpen={false}
            />
          </>
        ) : (
          <SubmittedResponsesSection
            title={t('orderDetails.submittedAnswers')}
            responses={service.clientSurveyResponses ?? []}
            locale={locale}
            templateQuestionsForLookup={templateQuestionsForLookup}
            accent="green"
          />
        )}

      </div>
    </div>
  )
}

export function OrderSurveysTab({ order, locale }: { order: OrderDetails; locale: 'en' | 'ar' }) {
  const { t } = useLocalization()
  const [expandedClientAnswersServiceIds, setExpandedClientAnswersServiceIds] = useState<Set<string>>(
    new Set()
  )

  const servicesWithSurveys = order.services.filter(serviceHasSurveyContent)
  const totalPendingSurveys = order.services.reduce((acc, s) => acc + (s.pendingSurveyCount || 0), 0)
  const totalAnswers = order.services.reduce(
    (acc, s) => acc + (s.clientAnswersWithQuestions?.length || 0),
    0
  )
  const totalResponses = order.services.reduce(
    (acc, s) => acc + (s.clientSurveyResponses?.length || 0),
    0
  )

  const toggleClientAnswersExpanded = (serviceId: string) => {
    setExpandedClientAnswersServiceIds((prev) => {
      const next = new Set(prev)
      if (next.has(serviceId)) next.delete(serviceId)
      else next.add(serviceId)
      return next
    })
  }

  if (servicesWithSurveys.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
          <MessageSquare className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{t('orderDetails.noSurveys')}</h3>
        <p className="mt-1 text-gray-500">{t('orderDetails.noSurveysYet')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-5 text-center">
          <p className="text-3xl font-bold text-yellow-600">{totalPendingSurveys}</p>
          <p className="text-sm text-gray-500 mt-1">{t('orderDetails.pendingSurveys')}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 text-center">
          <p className="text-3xl font-bold text-brand-primary">{totalAnswers}</p>
          <p className="text-sm text-gray-500 mt-1">{t('orderDetails.clientAnswers')}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 text-center">
          <p className="text-3xl font-bold text-green-600">{totalResponses}</p>
          <p className="text-sm text-gray-500 mt-1">{t('orderDetails.submittedAnswers')}</p>
        </div>
      </div>

      <div className="grid gap-4">
        {servicesWithSurveys.map((service, index) => (
          <ServiceSurveyCard
            key={service.id}
            service={service}
            index={index}
            locale={locale}
            expandedClientAnswers={expandedClientAnswersServiceIds.has(service.id)}
            onToggleClientAnswers={() => toggleClientAnswersExpanded(service.id)}
          />
        ))}
      </div>
    </div>
  )
}
